import { CONFIG } from '../config';
import { fromEmoji } from '../icons/legacy';
import { interiorDef, isFloor, isWall } from '../content/interior';
import { isRide } from '../content/rides';
import { clamp } from '../core/math';
import { createInitialState, DEFAULT_LOOK, emptyInventory, SAVE_VERSION } from './defaults';
import { LOTTO, validLottoTicket } from './rules';
import { ITEM_IDS, STAT_IDS, type FeedPost, type GameState, type MemeImage } from './types';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const num = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

/**
 * Parses and sanitizes saved JSON. Unknown/missing fields fall back to defaults so
 * older saves keep loading as the game grows. Returns null for unusable data.
 */
export function deserialize(json: string): GameState | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isObj(raw) || typeof raw.version !== 'number' || raw.version > SAVE_VERSION) return null;
  if (!isObj(raw.player) || !isObj(raw.time)) return null;

  const name = typeof raw.player.name === 'string' ? raw.player.name.slice(0, 24) : 'Bubba';
  const look = { ...DEFAULT_LOOK, ...(isObj(raw.player.look) ? raw.player.look : {}) };
  const base = createInitialState({ name, look, seed: num(raw.seed, 1) });

  const inv = emptyInventory();
  if (isObj(raw.inv)) for (const id of ITEM_IDS) inv[id] = Math.max(0, Math.floor(num(raw.inv[id], 0)));

  const stats = { ...base.stats };
  if (isObj(raw.stats)) for (const id of STAT_IDS) stats[id] = clamp(num(raw.stats[id], stats[id]), 0, 100);

  const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);

  return {
    ...base,
    money: num(raw.money, base.money),
    debt: Math.max(0, num(raw.debt, 0)),
    clout: Math.max(0, num(raw.clout, 0)),
    stats,
    inv,
    time: {
      day: Math.max(1, Math.floor(num(raw.time.day, 1))),
      minutes: clamp(num(raw.time.minutes, CONFIG.wakeMinutes), 0, 1439),
    },
    rent: isObj(raw.rent)
      ? {
          paidThroughWeek: Math.max(0, Math.floor(num(raw.rent.paidThroughWeek, 0))),
          strikes: clamp(Math.floor(num(raw.rent.strikes, 0)), 0, CONFIG.maxStrikes),
        }
      : base.rent,
    decor: strings(raw.decor),
    interior: isObj(raw.interior)
      ? {
          items: strings(raw.interior.items).filter((id) => !!interiorDef(id)),
          stored: strings(raw.interior.stored).filter((id) => !!interiorDef(id)),
          walls:
            typeof raw.interior.walls === 'string' && isWall(raw.interior.walls)
              ? raw.interior.walls
              : 'paneling',
          floor:
            typeof raw.interior.floor === 'string' && isFloor(raw.interior.floor)
              ? raw.interior.floor
              : 'shag',
        }
      : base.interior,
    fund: Math.max(0, num(raw.fund, 0)),
    rides: strings(raw.rides).filter((id) => isRide(id) && id !== 'camaro'),
    ride:
      isRide(raw.ride) && (raw.ride === 'camaro' || strings(raw.rides).includes(raw.ride))
        ? raw.ride
        : 'camaro',
    lotto:
      isObj(raw.lotto) && Array.isArray(raw.lotto.tickets)
        ? {
            week: Math.max(1, Math.floor(num(raw.lotto.week, 1))),
            tickets: (raw.lotto.tickets as unknown[])
              .filter((t): t is number[] => Array.isArray(t) && validLottoTicket(t as number[]))
              .slice(0, LOTTO.maxTickets),
          }
        : base.lotto,
    heirlooms: Array.isArray(raw.heirlooms) ? strings(raw.heirlooms) : base.heirlooms,
    camaro: clamp(num(raw.camaro, 0), 0, 100),
    achievements: strings(raw.achievements),
    counters: isObj(raw.counters) ? (raw.counters as GameState['counters']) : {},
    flags: isObj(raw.flags) ? (raw.flags as GameState['flags']) : {},
    feed: Array.isArray(raw.feed) ? (raw.feed as GameState['feed']).slice(0, 40).map(migratePost) : [],
    nextPostId: Math.max(1, Math.floor(num(raw.nextPostId, 1))),
    // Jobs are tied to transient world props, so they don't survive a reload.
    job: null,
    status: raw.status === 'evicted' || raw.status === 'won' ? raw.status : 'playing',
  };
}

/**
 * Posts saved before the game had its own icons have real emoji in them (and memes kept
 * theirs in an `emoji` field): swap them for :icon: tokens.
 */
function migratePost(post: FeedPost): FeedPost {
  const meme = post.meme as (MemeImage & { emoji?: unknown }) | undefined;
  const text = (v: unknown): string => (typeof v === 'string' ? fromEmoji(v) : '');
  const out: FeedPost = { ...post, author: text(post.author), text: text(post.text) };
  if (meme)
    out.meme = {
      icon: typeof meme.icon === 'string' ? meme.icon : text(meme.emoji),
      top: text(meme.top),
      bottom: text(meme.bottom),
      bg: typeof meme.bg === 'string' ? meme.bg : '#2b2b2b',
    };
  return out;
}

export function serialize(state: GameState): string {
  return JSON.stringify(state);
}

export function saveGame(state: GameState, storage: StorageLike | null = safeStorage()): boolean {
  if (!storage) return false;
  try {
    storage.setItem(CONFIG.saveKey, serialize(state));
    return true;
  } catch {
    return false;
  }
}

export function loadGame(storage: StorageLike | null = safeStorage()): GameState | null {
  if (!storage) return null;
  try {
    const json = storage.getItem(CONFIG.saveKey);
    return json ? deserialize(json) : null;
  } catch {
    return null;
  }
}

export function clearSave(storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.removeItem(CONFIG.saveKey);
  } catch {
    // Storage can be unavailable (private mode); nothing to clear.
  }
}
